/**
 * transportVehicle.ts — 貸出のお客様を運ぶ車の表示と、その保存（車両ポジション。2026-11-04）
 *
 * 状態は増やさず、既存の保存先から表示を作る:
 *   送迎車   … 予約 json_data「到着連絡: 有り / 空港: 送迎車」
 *   別車両   … 上に加えて予約 json_data「別車両番号: 1234」（新規はこのキーだけ）
 *   返却車   … 予約 json_data「到着連絡: 有り / 空港: 返却車」+ 車両 json_data「返却送迎」（予約ID）
 * 別車両番号は 空港 が 送迎車 のときだけ意味を持つ。ほかの値にする保存では stripStaleAltShuttle で落とす。
 * 仕様: md/master_md/page-daylist.md「お客様を運ぶ車」
 */
import { extractJsonDataRecord } from './hotelDelivery';
import { ARRIVAL_CONTACT_KEY, AIRPORT_KEY, ARRIVAL_CONTACT_ON } from './arrivalContact';
import { shortVehicleCode, toDigits } from './displayFormat';

export const ALT_SHUTTLE_KEY = '別車両番号';
export const AIRPORT_SHUTTLE = '送迎車';
export const AIRPORT_RETURN_CAR = '返却車';
const ALT_NUMBER_MAX = 6;

/** 入力を車両番号にする（全角→半角・数字のみ・6桁まで） */
export const normalizeAltShuttleNumber = (raw: string): string => toDigits(raw).slice(0, ALT_NUMBER_MAX);

/** 予約の別車両番号（空港が送迎車のときだけ。無ければ ''） */
export function getAltShuttleNumber(reservation: { json_data?: unknown; detail?: { json_data?: unknown } | null } | null | undefined): string {
  const data = extractJsonDataRecord(reservation?.json_data ?? reservation?.detail?.json_data ?? null);
  if (!data || data[AIRPORT_KEY] !== AIRPORT_SHUTTLE) return '';
  return String(data[ALT_SHUTTLE_KEY] ?? '').trim();
}

/** 車両カードに出す文言。送迎車 / 別1234 / 返8118（返却送迎の車が分からなければ 返却車）/ 該当なしは null */
export function transportLabel(input: {
  on: boolean; airport: string; altNumber?: string; returnCarCode?: string | null;
}): string | null {
  if (!input.on) return null;
  if (input.airport === AIRPORT_SHUTTLE) return input.altNumber ? `別${input.altNumber}` : AIRPORT_SHUTTLE;
  if (input.airport === AIRPORT_RETURN_CAR) return input.returnCarCode ? `返${shortVehicleCode(input.returnCarCode)}` : AIRPORT_RETURN_CAR;
  return null;
}

const baseJson = (jd: any): { data: Record<string, any>; types: Record<string, any>; rest: any } => {
  const j = jd && typeof jd === 'object' && !Array.isArray(jd) ? jd : {};
  return { data: { ...(j.data || {}) }, types: { ...(j.types || {}) }, rest: j };
};

/** 送迎車（number を渡すと別車両）にした json_data。空港1Fの「送迎車」サブエリアに移る */
export function withShuttleContact(jd: any, number?: string): any {
  const { data, types, rest } = baseJson(jd);
  data[ARRIVAL_CONTACT_KEY] = ARRIVAL_CONTACT_ON;
  data[AIRPORT_KEY] = AIRPORT_SHUTTLE;
  if (number) {
    data[ALT_SHUTTLE_KEY] = number;
    types[ALT_SHUTTLE_KEY] = 'text';
  } else {
    delete data[ALT_SHUTTLE_KEY];
    delete types[ALT_SHUTTLE_KEY];
  }
  return { ...rest, data, types };
}

/** 送迎車・別車両を取り消した json_data。予約は予約リストへ戻る */
export function withoutShuttleContact(jd: any): any {
  const { data, types, rest } = baseJson(jd);
  delete data[ARRIVAL_CONTACT_KEY];
  delete data[AIRPORT_KEY];
  delete data[ALT_SHUTTLE_KEY];
  delete types[ALT_SHUTTLE_KEY];
  return { ...rest, data, types };
}

/** data から、空港が送迎車でないのに残っている別車両番号を消す（連絡モーダル等の保存前に通す） */
export function dropStaleAltShuttleKey(data: Record<string, any>): void {
  if (data && data[ALT_SHUTTLE_KEY] !== undefined && data[AIRPORT_KEY] !== AIRPORT_SHUTTLE) {
    delete data[ALT_SHUTTLE_KEY];
  }
}

/** 予約の保存ペイロードから古い別車両番号を落とす。該当しなければ input をそのまま返す */
export function stripStaleAltShuttle<T extends { json_data?: any; detail?: any }>(input: T): T {
  const jd = input?.json_data;
  const data = jd && typeof jd === 'object' ? jd.data : null;
  if (!data || data[ALT_SHUTTLE_KEY] === undefined || data[AIRPORT_KEY] === AIRPORT_SHUTTLE) return input;
  const nextData = { ...data };
  delete nextData[ALT_SHUTTLE_KEY];
  const types = { ...(jd.types || {}) };
  delete types[ALT_SHUTTLE_KEY];
  const next = { ...jd, data: nextData, types };
  return { ...input, json_data: next, ...(input.detail ? { detail: { ...input.detail, json_data: next } } : {}) };
}
